import { Routes, Route, RoutesProps, Navigate } from "react-router-dom";

import React, { Suspense } from "react";
import ProtectedRoute from "./ProtectedRoute";
import Main from "../components/Main";
import AdminSetPassword from "../components/Login/AdminSetPassword";

const Navigation = () => {
  interface ProtectedRouteProps extends RoutesProps {
    authenticationPath: string;
  }
  const defaultProtectedRouteProps: Omit<ProtectedRouteProps, "outlet"> = {
    authenticationPath: "/login",
  };

  const Login = React.lazy(() => import("../components/Login/Login"));

  const ForgotPassword = React.lazy(
    () => import("../components/Login/ForgotPassword")
  );

  const Dashboard = React.lazy(
    () => import("../components/Dashboard/Dashboard")
  );

  const AiSourcingAnalytics = React.lazy(
    () => import("../components/AiSourcingAnalytics/AiSourcingAnalytics")
  );

  const AiSessionMonitor = React.lazy(
    () => import("../components/AiSessionMonitor/AiSessionMonitor")
  );

  const AiSessions = React.lazy(
    () => import("../components/AiSessionMonitor/AiSessions")
  );

  const Profile = React.lazy(
    () => import("../components/Profile/Profile")
  );

  const QuestionList = React.lazy(() => import("../components/ManageQuestions/QuestionList"));
  const SkillList = React.lazy(() => import("../components/ManageSkill/SkillList"));
  const LanguageList = React.lazy(() => import("../components/ManageLanguage/LanguageList"));
  const QuestionSetList = React.lazy(() => import("../components/ManageQuestionSet/QuestionSetList"));
  const AddQuestion = React.lazy(() => import("../components/ManageQuestions/AddQuestion"));
  const AddQuestionSet = React.lazy(() => import("../components/ManageQuestionSet/AddQuestionset"));
  const AddExistingQuestion = React.lazy(() => import("../components/ManageQuestionSet/AddExistingQuestionSet"));
  const RoleList = React.lazy(() => import("../components/ManageRole/RoleList"));
  const ResumeAnalysis = React.lazy(() => import("../components/ManageResumeAnalysis/ResumeAnalysisList"));
  const JDAnalysis = React.lazy(() => import("../components/ManageJDAnalysis/JDAnalysisList"));
  const AnswerRatingAnalysis = React.lazy(() => import("../components/ManageAnswerRatingAnalysis/AnswerRatingAnalysis"));
  const SetPassword = React.lazy(() => import("../components/Login/SetPassword"));
  const QuestionAnalysis = React.lazy(() => import("../components/ManageQuestionAnalysis/QuestionAnalysisListing"));
  const UsersList = React.lazy(() => import("../components/ManageUser/UserListing"));
  const PlansList = React.lazy(() => import("../components/ManagePlans/List"));
  const AddPlan = React.lazy(() => import("../components/ManagePlans/Add"));
  const CompaniesList = React.lazy(() => import("../components/ManageCompanies/List"));
  const CompanyDetails = React.lazy(() => import("../components/ManageCompanies/Details"));
  const Support = React.lazy(() => import("../components/Support/Support"));
  const AppVersionList = React.lazy(() => import("../components/ManageAppVersion/AppVersionList"));


  return (
    <>
      <div id="main-wraper">
        <Routes>
          <Route path="/reset-password/:token"
            element={
              <Suspense fallback={<></>}>
                <SetPassword />
              </Suspense>
            }
          />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/login"
            element={
              <Suspense fallback={<></>}>
                {" "}
                <Login />{" "}
              </Suspense>
            }
          />

          <Route
            path="/set-password/:token"
            element={
              <Suspense fallback={<></>}>
                <AdminSetPassword />
              </Suspense>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<></>}>
                {" "}
                <ForgotPassword />{" "}
              </Suspense>
            }
          />

          <Route
            path="/reset-password"
            element={
              <Suspense fallback={<></>}>
                <AdminSetPassword />
              </Suspense>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={<Main />}
              />
            }
          >

            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Dashboard />{" "}
                </Suspense>
              }
            />

            {/* Sits directly under the dashboard in the sidebar: it answers the
                same "what have we got" question, but about the external AI
                sourcing index rather than the platform's own candidates. */}
            <Route
              path="/ai-sourcing-analytics"
              element={
                <Suspense fallback={<></>}>
                  <AiSourcingAnalytics />
                </Suspense>
              }
            />

            {/* Next to the other two analytics screens, because it answers the
                third "what is going on" question — this one about work in
                flight rather than a stock of candidates — and it is where the
                reset that unsticks a candidate lives. */}
            <Route
              path="/ai-session-monitor"
              element={
                <Suspense fallback={<></>}>
                  <AiSessionMonitor />
                </Suspense>
              }
            />

            {/* The monitor's working list on its own, for the admin who came
                to clear the queue rather than to read the numbers above it. */}
            <Route
              path="/ai-sessions"
              element={
                <Suspense fallback={<></>}>
                  <AiSessions />
                </Suspense>
              }
            />

            <Route
              path="/profile"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Profile />{" "}
                </Suspense>
              }
            />

            <Route
              path="/skills"
              element={
                <Suspense fallback={<></>}>
                  <SkillList />
                </Suspense>
              }
            />

            <Route
              path="/questions"
              element={
                <Suspense fallback={<></>}>
                  <QuestionList />
                </Suspense>
              }
            />

            <Route
              path="/add-questions"
              element={
                <Suspense fallback={<></>}>
                  <AddQuestion />
                </Suspense>
              }
            />

            <Route
              path="/edit-questions"
              element={
                <Suspense fallback={<></>}>
                  <AddQuestion />
                </Suspense>
              }
            />

            <Route
              path="/languages"
              element={
                <Suspense fallback={<></>}>
                  <LanguageList />
                </Suspense>
              }
            />

            <Route
              path="/questions-sets"
              element={
                <Suspense fallback={<></>}>
                  <QuestionSetList />
                </Suspense>
              }
            />

            <Route
              path="/add-question-set"
              element={
                <Suspense fallback={<></>}>
                  <AddQuestionSet />
                </Suspense>
              }
            />

            <Route
              path="/view-question-set"
              element={
                <Suspense fallback={<></>}>
                  <QuestionList />
                </Suspense>
              }
            />

            <Route
              path="/add-existing"
              element={
                <Suspense fallback={<></>}>
                  <AddExistingQuestion />
                </Suspense>
              }
            />
            <Route
              path="/role-list"
              element={
                <Suspense fallback={<></>}>
                  <RoleList />
                </Suspense>
              }
            />
            <Route
              path="/resume-analysis"
              element={
                <Suspense fallback={<></>}>
                  <ResumeAnalysis />
                </Suspense>
              }
            />
            <Route
              path="/jd-analysis"
              element={
                <Suspense fallback={<></>}>
                  <JDAnalysis />
                </Suspense>}
            />
            <Route
              path="/answer-rating-analysis"
              element={
                <Suspense fallback={<></>}>
                  <AnswerRatingAnalysis />
                </Suspense>
              }
            />
            <Route
              path="/question-analysis"
              element={
                <Suspense fallback={<></>}>
                  <QuestionAnalysis />
                </Suspense>
              }
            />
            <Route
              path="/users"
              element={
                <Suspense fallback={<></>}>
                  <UsersList />
                </Suspense>
              }
            />
            <Route
              path="/plans"
              element={
                <Suspense fallback={<></>}>
                  <PlansList />
                </Suspense>
              }
            />

            <Route
              path="/add-plan"
              element={
                <Suspense fallback={<></>}>
                  <AddPlan />
                </Suspense>
              }
            />

            <Route
              path="/companies"
              element={
                <Suspense fallback={<></>}>
                  <CompaniesList />
                </Suspense>
              }
            />

            <Route
              path="/company-details/:company_uuid"
              element={
                <Suspense fallback={<></>}>
                  <CompanyDetails />
                </Suspense>
              }
            />

            <Route
              path="/client-portal-support"
              element={
                <Suspense fallback={<></>}>
                  <Support />
                </Suspense>
              }
            />

            {/* Mobile releases: history plus the force-update rules. */}
            <Route
              path="/app-versions"
              element={
                <Suspense fallback={<></>}>
                  <AppVersionList />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </div>
    </>
  );
};

export default Navigation;
